import { useState } from 'react'
import type { UserRolesState } from '@/hooks/useUserRoles'
import styles from './RoleManagement.module.css'

export type RoleManagementProps = {
  state: UserRolesState
  /** The signed-in admin's own oid — used to label their row and to block
   * self-demotion (the server also rejects it with 409). */
  currentOid: string
  /** Last grant-by-email failure to surface (unknown email, directory down). */
  grantError?: string | null
  onGrant: (email: string) => void
  onRevoke: (oid: string) => void
}

export default function RoleManagement({
  state,
  currentOid,
  grantError,
  onGrant,
  onRevoke,
}: RoleManagementProps) {
  const [emailInput, setEmailInput] = useState('')

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const trimmed = emailInput.trim()
    if (trimmed === '') return
    onGrant(trimmed)
    setEmailInput('')
  }

  return (
    <section className={styles.section} aria-labelledby="role-management-heading">
      <h2 id="role-management-heading" className={styles.heading}>
        Tilgangsstyring
      </h2>
      <p className={styles.lead}>
        Administratorer kan forvalte tilganger og kildemateriale. Brukere uten en rad her er
        vanlige brukere.
      </p>

      <form className={styles.grantForm} onSubmit={handleSubmit}>
        <label className={styles.label} htmlFor="grant-email">
          Gi admin-tilgang (e-postadresse)
        </label>
        <div className={styles.grantRow}>
          <input
            id="grant-email"
            type="email"
            className={styles.input}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="navn@ortopediteknikk.no"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="submit" className={styles.grantButton} disabled={emailInput.trim() === ''}>
            Gi admin
          </button>
        </div>
      </form>

      {grantError != null && (
        <p className={styles.error} role="status">
          {grantError}
        </p>
      )}

      {renderList(state, currentOid, onRevoke)}
    </section>
  )
}

function renderList(
  state: UserRolesState,
  currentOid: string,
  onRevoke: (oid: string) => void,
): React.ReactNode {
  if (state.kind === 'loading') {
    return <p className={styles.muted}>Laster tilganger…</p>
  }
  if (state.kind === 'error') {
    return <p className={styles.error}>Kunne ikke laste tilganger: {state.message}</p>
  }
  if (state.roles.length === 0) {
    return <p className={styles.muted}>Ingen tildelte roller ennå.</p>
  }
  return (
    <ul className={styles.list}>
      {state.roles.map((role) => {
        const isSelf = role.oid === currentOid
        return (
          <li key={role.oid} className={styles.row}>
            <div className={styles.rowMain}>
              <div className={styles.identity}>
                {role.email != null && role.email !== '' ? (
                  <>
                    <span className={styles.email}>
                      {role.email}
                      {isSelf ? ' (deg)' : ''}
                    </span>
                    <span className={styles.oid}>{role.oid}</span>
                  </>
                ) : (
                  <span className={styles.oid}>
                    {role.oid}
                    {isSelf ? ' (deg)' : ''}
                  </span>
                )}
              </div>
              <span
                className={`${styles.badge} ${role.role === 'admin' ? styles.badgeAdmin : ''}`}
              >
                {role.role}
              </span>
            </div>
            <span className={styles.meta}>Lagt til av {role.addedBy}</span>
            {role.role === 'admin' && (
              <button
                className={styles.revoke}
                aria-disabled={isSelf}
                title={isSelf ? 'Du kan ikke fjerne din egen admin-tilgang' : undefined}
                onClick={() => {
                  if (!isSelf) onRevoke(role.oid)
                }}
              >
                Fjern admin
              </button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
